import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useEffect, useState } from 'react';

export type SelectItem = {
  id: string;
  name: string;
};

/**
 * Props for the SelectComponent
 */
type SelectComponentProps = {
  className?: string;
  label?: string;
  items: SelectItem[];
  initialSelection?: SelectItem;
  handleSelectedValues: (value: SelectItem | null) => void;
  required?: boolean;
};

/**
 * MUI Select Component
 * @param className Optional class name for styling
 * @param label Optional label for the input field
 * @param items The items to select from
 * @param initialSelection Optional initial selection of items
 * @param handleSelectedValues Callback function to handle selected values
 * @param required Whether the select field is required
 * @returns
 */
function SelectComponent({
  className = '',
  label = 'Sélectionner des éléments',
  items,
  initialSelection = undefined,
  handleSelectedValues,
  required = true,
}: SelectComponentProps) {
  const [value, setValue] = useState<SelectItem | null>(
    initialSelection ?? null
  );

  useEffect(() => {
    if (
      initialSelection !== undefined &&
      (initialSelection.id !== value?.id ||
        initialSelection.name !== value?.name)
    ) {
      setValue(initialSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSelection?.id, initialSelection?.name]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    const selectedItem = items.find((item) => item.id === selectedId) || null;
    setValue(selectedItem);
    handleSelectedValues(selectedItem);
  };

  return (
    <FormControl
      fullWidth
      margin="normal"
      className={className}
      required={required}
    >
      <InputLabel
        id={`select-label-${label.replace(/\s+/g, '-').toLowerCase()}`}
      >
        {label}
      </InputLabel>
      <Select
        className={className}
        labelId={`select-label-${label.replace(/\s+/g, '-').toLowerCase()}`}
        label={label}
        value={value ? value.id : ''}
        variant="outlined"
        onChange={handleChange}
      >
        {items.map((item) => (
          <MenuItem key={item.id} value={item.id}>
            {item.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default SelectComponent;
